import React, { useEffect, useRef, useState } from 'react';
import './DvdBouncer.css';

export default function DvdBouncer({ imgSrc, label, speed }) {
  const containerRef = useRef(null);
  const itemRef = useRef(null);

  // position & velocity refs
  const pos = useRef({ x: 48, y: 48 });
  const vel = useRef({ vx: speed * 0.8, vy: speed * 0.6 });
  const lastTs = useRef(null);

  const [bounces, setBounces] = useState(0);
  const [reduced, setReduced] = useState(false);
  const rafRef = useRef(null);

  // Respect reduced motion
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  // Randomize initial direction slightly (same speed magnitude)
  useEffect(() => {
    const angle = Math.random() * Math.PI * 2;
    vel.current.vx = Math.cos(angle) * speed;
    vel.current.vy = Math.sin(angle) * speed;
  }, [speed]);

  // Pause when tab is hidden to avoid giant jumps
  useEffect(() => {
    const onVisibility = () => {
      lastTs.current = null;
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Animation loop
  useEffect(() => {
    if (reduced) return;

    const tick = (ts) => {
      if (!lastTs.current) lastTs.current = ts;
      const dt = Math.min((ts - lastTs.current) / 1000, 0.05); // clamp large gaps
      lastTs.current = ts;

      const container = containerRef.current;
      const item = itemRef.current;
      if (!container || !item) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const bounds = { w: container.clientWidth, h: container.clientHeight };
      const size = { w: item.offsetWidth, h: item.offsetHeight };

      let nx = pos.current.x + vel.current.vx * dt;
      let ny = pos.current.y + vel.current.vy * dt;

      let hitX = false;
      let hitY = false;

      // Left/Right
      if (nx <= 0) { nx = 0; vel.current.vx *= -1; hitX = true; }
      else if (nx + size.w >= bounds.w) {
        nx = Math.max(0, bounds.w - size.w);
        vel.current.vx *= -1; hitX = true;
      }

      // Top/Bottom
      if (ny <= 0) { ny = 0; vel.current.vy *= -1; hitY = true; }
      else if (ny + size.h >= bounds.h) {
        ny = Math.max(0, bounds.h - size.h);
        vel.current.vy *= -1; hitY = true;
      }

      pos.current.x = nx;
      pos.current.y = ny;
      item.style.transform = `translate(${nx}px, ${ny}px)`;

      if (hitX || hitY) setBounces((c) => c + (hitX && hitY ? 2 : 1));

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [reduced]);

  // Keep within bounds on resize
  useEffect(() => {
    const onResize = () => {
      const container = containerRef.current;
      const item = itemRef.current;
      if (!container || !item) return;
      const maxX = Math.max(0, container.clientWidth - item.offsetWidth);
      const maxY = Math.max(0, container.clientHeight - item.offsetHeight);
      pos.current.x = Math.min(Math.max(0, pos.current.x), maxX);
      pos.current.y = Math.min(Math.max(0, pos.current.y), maxY);
      item.style.transform = `translate(${pos.current.x}px, ${pos.current.y}px)`;
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div className="dvd-container" ref={containerRef}>
      {!reduced && (
        <>
          <div className="dvd-item" ref={itemRef} aria-label="DVD bouncing logo">
            {imgSrc && <img className="dvd-img" src={imgSrc} alt="" />}
            {label && <div className="dvd-label">{label}</div>}
          </div>
          <div className="dvd-counter" role="status" aria-live="polite">
            {bounces}
          </div>
        </>
      )}

      {reduced && (
        <div className="dvd-reduced">
          {imgSrc && <img className="dvd-img" src={imgSrc} alt="" />}
          {label && <div className="dvd-label">{label}</div>}
          <div className="dvd-counter-static">Motion reduced</div>
        </div>
      )}
    </div>
  );
}
